import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const api = "method/restaurant_management.customer_balances.";
export const canManageBalances = (settings, profile) => {
    const allowed = (settings?.customer_balance_role_profiles || [])
        .map((r) => r.role_profile)
        .filter(Boolean);
    return (
        Number(settings?.enable_customer_balance_manager) === 1 &&
        (!allowed.length || allowed.includes(profile))
    );
};
const escapeHtml = (value) =>
    String(value ?? "").replace(
        /[&<>"']/g,
        (c) =>
            ({
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': "&quot;",
                "'": "&#39;",
            }[c])
    );
export const receiptHtml = (receipt, title, labels = {}) =>
    `<html><head><title>${escapeHtml(
        title
    )}</title><style>body{font:16px Arial;padding:20px;max-width:360px}h1{font-size:22px}p{line-height:1.5}</style></head><body><h1>${escapeHtml(
        title
    )}</h1>${["name", "company", "date", "customer_name", "mode"]
        .map(
            (k) =>
                `<p><strong>${escapeHtml(
                    labels[k] || k
                )}:</strong> ${escapeHtml(receipt[k])}</p>`
        )
        .join("")}<h2>${escapeHtml(receipt.amount)} ${escapeHtml(
        receipt.currency
    )}</h2><p>${escapeHtml(labels.balance || "Balance")}: ${escapeHtml(
        receipt.balance
    )} ${escapeHtml(receipt.currency)}</p></body></html>`;
function message(error) {
    try {
        return JSON.parse(
            JSON.parse(error.response.data._server_messages)[0]
        ).message.replace(/<[^>]*>/g, "");
    } catch (_) {
        return (
            error.response?.data?.message ||
            "Unable to complete this request. Please try again."
        );
    }
}
export default function CustomerBalanceManager({ open, onClose, posProfile }) {
    const { t } = useTranslation();
    const label = (key) => t(`customerBalances.${key}`);
    const [context, setContext] = useState(null),
        [rows, setRows] = useState([]),
        [search, setSearch] = useState(""),
        [next, setNext] = useState(null);
    const [selected, setSelected] = useState(null),
        [amount, setAmount] = useState(""),
        [mode, setMode] = useState("");
    const [busy, setBusy] = useState(false),
        [error, setError] = useState(""),
        [receipt, setReceipt] = useState(null),
        [confirm, setConfirm] = useState(false);
    const [invoices, setInvoices] = useState([]), [allocations, setAllocations] = useState({}), [pendingPos, setPendingPos] = useState([]);
    const request = useRef(null),
        paying = useRef(false),
        generation = useRef(0);
    const load = async (start = 0) => {
        const token = ++generation.current;
        setBusy(true);
        setError("");
        try {
            const [c, d] = await Promise.all([
                axios.get(api + "get_context", {
                    params: { pos_profile: posProfile },
                }),
                axios.get(api + "customers", {
                    params: { pos_profile: posProfile, search, start },
                }),
            ]);
            if (token !== generation.current) return;
            setContext(c.data.message);
            setRows((prev) =>
                start ? [...prev, ...d.data.message.rows] : d.data.message.rows
            );
            setNext(d.data.message.next_start);
            setMode((prev) =>
                c.data.message.modes.some((m) => m.name === prev)
                    ? prev
                    : c.data.message.modes[0]?.name || ""
            );
        } catch (e) {
            if (token === generation.current) setError(message(e));
        } finally {
            if (token === generation.current) setBusy(false);
        }
    };
    useEffect(() => {
        if (open) {
            setSelected(null);
            setReceipt(null);
            setConfirm(false);
            request.current = null;
            load();
        }
        return () => {
            generation.current++;
        };
    }, [open, posProfile]); // search runs explicitly
    const selectCustomer = async (customer) => {
        const token = ++generation.current;
        setSelected(customer); setInvoices([]); setAllocations({}); setPendingPos([]);
        setAmount(""); setConfirm(false); setReceipt(null); setError(""); setBusy(true);
        try {
            const { data } = await axios.get(api + "unpaid_invoices", { params: { pos_profile: posProfile, customer: customer.customer } });
            if (token !== generation.current) return;
            setInvoices(data.message.rows); setPendingPos(data.message.pending_pos || []);
        } catch (e) { if (token === generation.current) setError(message(e)); }
        finally { if (token === generation.current) setBusy(false); }
    };
    const changeAllocation = (invoice, value) => {
        const next = { ...allocations };
        if (value === null) delete next[invoice]; else next[invoice] = value;
        setAllocations(next);
        setAmount(Object.values(next).reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(2));
        setConfirm(false);
    };
    const invalidAllocation = !Object.keys(allocations).length || invoices.some(r =>
        Object.prototype.hasOwnProperty.call(allocations, r.name) &&
        (!Number.isFinite(Number(allocations[r.name])) || Number(allocations[r.name]) <= 0 || Number(allocations[r.name]) > r.outstanding_amount));
    const receive = async () => {
        if (paying.current) return;
        paying.current = true;
        setBusy(true);
        setError("");
        const payload = {
            pos_profile: posProfile,
            customer: selected.customer,
            amount: Number(amount),
            mode_of_payment: mode,
            allocations: Object.entries(allocations).sort(([a],[b]) => a.localeCompare(b)).map(([invoice, amount]) => ({ invoice, amount: Number(amount) })),
        };
        const signature = JSON.stringify(payload);
        if (!request.current) {
            try {
                request.current = JSON.parse(
                    sessionStorage.getItem("customer-receipt-pending")
                );
            } catch (_) {}
        }
        if (request.current?.signature !== signature)
            request.current = { signature, id: crypto.randomUUID() };
        try {
            sessionStorage.setItem(
                "customer-receipt-pending",
                JSON.stringify(request.current)
            );
            const result = await axios.post(api + "receive", {
                ...payload,
                request_id: request.current.id,
            });
            setReceipt(result.data.message);
            setSelected(null);
            setConfirm(false);
            request.current = null;
            sessionStorage.removeItem("customer-receipt-pending");
            await load();
        } catch (e) {
            setError(message(e));
        } finally {
            paying.current = false;
            setBusy(false);
        }
    };
    const print = () => {
        const win = window.open("", "_blank");
        if (!win) {
            setError(label("allowPopup"));
            return;
        }
        win.document.write(
            receiptHtml(
                receipt,
                label("receipt"),
                Object.fromEntries(
                    [
                        "name",
                        "company",
                        "date",
                        "customer_name",
                        "mode",
                        "balance",
                    ].map((k) => [k, label(k)])
                )
            )
        );
        win.document.close();
        win.focus();
        win.print();
    };
    return (
        <Dialog
            open={open}
            onClose={() => !busy && onClose()}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{label("title")}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {receipt && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {label("saved")} {receipt.name} · {receipt.amount}{" "}
                        {receipt.currency}{" "}
                        <Button onClick={print}>{label("print")}</Button>
                    </Alert>
                )}
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {label("help")}
                </Typography>
                <Stack direction="row" gap={1}>
                    <TextField
                        label={label("search")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <Button disabled={busy} onClick={() => load()}>
                        {label("searchAction")}
                    </Button>
                </Stack>
                {busy && <CircularProgress size={24} sx={{ my: 2 }} />}
                {selected ? (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6">
                            {selected.customer_name}
                        </Typography>
                        <Typography>
                            {label("balance")}: {selected.balance.toFixed(2)}{" "}
                            {context?.currency}
                        </Typography>
                        <Typography sx={{ mt: 2 }}>{label("selectInvoices")}</Typography>
                        {pendingPos.length > 0 && <Alert severity="info" sx={{ my: 1 }}>{label("pendingPos")} ({pendingPos.map(r => r.name).join(", ")})</Alert>}
                        {!busy && !invoices.length && <Alert severity="info">{label("noInvoices")}</Alert>}
                        <Table size="small">
                            <TableHead><TableRow><TableCell>{label("select")}</TableCell><TableCell>{label("invoice")}</TableCell><TableCell>{label("outstanding")}</TableCell><TableCell>{label("allocate")}</TableCell></TableRow></TableHead>
                            <TableBody>{invoices.map(r => <TableRow key={r.name}>
                                <TableCell><Checkbox disabled={busy} inputProps={{ "aria-label": `${label("select")} ${r.name}` }} checked={Object.prototype.hasOwnProperty.call(allocations,r.name)} onChange={e => changeAllocation(r.name,e.target.checked ? r.outstanding_amount : null)} /></TableCell>
                                <TableCell>{r.name}<Typography variant="caption" display="block">{r.posting_date}</Typography></TableCell>
                                <TableCell>{Number(r.outstanding_amount).toFixed(2)}</TableCell>
                                <TableCell><TextField type="number" size="small" disabled={busy || !Object.prototype.hasOwnProperty.call(allocations,r.name)} value={allocations[r.name] ?? ""} onChange={e => changeAllocation(r.name,e.target.value)} inputProps={{"aria-label": `${label("allocate")} ${r.name}`,min:0.01,max:r.outstanding_amount,step:0.01}} /></TableCell>
                            </TableRow>)}</TableBody>
                        </Table>
                        <Button disabled={busy} onClick={() => selectCustomer(selected)}>{label("refreshInvoices")}</Button>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            gap={2}
                            sx={{ my: 2 }}
                        >
                            <TextField
                                label={label("amount")}
                                type="number"
                                value={amount}
                                InputProps={{ readOnly: true }}
                                disabled={busy}
                                inputProps={{
                                    min: 0.01,
                                    step: 0.01,
                                    max: selected.balance,
                                }}
                            />
                            <TextField
                                label={label("mode")}
                                select
                                value={mode}
                                onChange={(e) => {
                                    setMode(e.target.value);
                                    setConfirm(false);
                                }}
                                disabled={busy}
                                sx={{ minWidth: 200 }}
                            >
                                {context?.modes.map((m) => (
                                    <MenuItem key={m.name} value={m.name}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                        {!context?.modes.length && (
                            <Alert severity="warning">{label("noMode")}</Alert>
                        )}
                        {confirm && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {label("confirm")} {Number(amount).toFixed(2)}{" "}
                                {context?.currency} — {selected.customer_name}
                            </Alert>
                        )}
                        <Button
                            disabled={
                                busy ||
                                !mode || invalidAllocation ||
                                !Number.isFinite(Number(amount)) ||
                                Number(amount) <= 0 ||
                                Number(amount) > selected.balance
                            }
                            variant="contained"
                            onClick={() =>
                                confirm ? receive() : setConfirm(true)
                            }
                        >
                            {confirm
                                ? label("confirmButton")
                                : label("receive")}
                        </Button>
                        <Button
                            disabled={busy}
                            onClick={() => {
                                setSelected(null);
                                setConfirm(false);
                            }}
                        >
                            {label("back")}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{label("customer")}</TableCell>
                                    <TableCell>
                                        {label("balance")} ({context?.currency})
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.customer}>
                                        <TableCell>
                                            {r.customer_name}
                                            <Typography
                                                variant="caption"
                                                display="block"
                                            >
                                                {r.customer}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {r.balance.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                disabled={busy}
                                                onClick={() => selectCustomer(r)}
                                            >
                                                {label("receive")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {!rows.length && !busy && (
                            <Typography sx={{ my: 3 }}>
                                {label("empty")}
                            </Typography>
                        )}
                        {next !== null && (
                            <Button onClick={() => load(next)} disabled={busy}>
                                {label("more")}
                            </Button>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button disabled={busy} onClick={onClose}>
                    {label("close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
